import React, { useContext, useEffect, useRef, useState } from 'react';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore lodash is in the parent dir
import _ from 'lodash';
import {
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  InfoIcon,
  MapIcon,
} from 'lucide-react';

import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { QuickQuoteCurrencyToggle } from 'components/pricing/QuickQuoteCurrencyToggle';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { Quoting, QuotingPortals } from 'types/enums/Integrations';
import { constructGoogleMapsUrl } from 'utils/googleMaps';
import { cn } from 'utils/shadcn';

import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { QuoteTypeInSource } from '../types';
import { toTitleCase } from '../utils';
import { getMileageSourceLabel } from '../utils/mileageSource';

export type DistanceSource = Quoting | QuotingPortals | 'pcmiler';

export const RouteDisplaySection = () => {
  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);

  const { quoteResponse, routeDetails, maxDistance, distanceSource } =
    useQuickQuoteContext();
  const [stopsExpanded, setStopsExpanded] = useState(false);
  const [currentStopIndex, setCurrentStopIndex] = useState(0);
  const stopsContainerRef = useRef<HTMLDivElement>(null);

  // Reset scroll position and expanded state when quote changes
  useEffect(() => {
    if (quoteResponse?.stops.length === 2 && stopsContainerRef.current) {
      stopsContainerRef.current.scrollLeft = 0;
    }
    // Reset expanded state when getting new quote
    setStopsExpanded(false);
    setCurrentStopIndex(0);
  }, [quoteResponse?.stops.length]);

  // Carousel navigation functions for route stops
  const scrollToStop = (index: number) => {
    if (!stopsContainerRef.current || !quoteResponse) return;
    if (index < 0 || index >= quoteResponse.stops.length) return;

    const container = stopsContainerRef.current;
    // Find all stop span elements (not arrows)
    // Select only the stop labels we render (exclude arrow icons/containers)
    const stopElements = container.querySelectorAll('.qq-stop-label');

    if (stopElements[index]) {
      const targetElement = stopElements[index] as HTMLElement;
      const containerRect = container.getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();

      // Calculate the scroll position to center the target element
      const scrollLeft =
        container.scrollLeft +
        (targetRect.left - containerRect.left) -
        containerRect.width / 2 +
        targetRect.width / 2;

      // Smooth scroll to the calculated position
      container.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });

      setCurrentStopIndex(index);
    }
  };

  const scrollLeft = () => scrollToStop(Math.max(0, currentStopIndex - 1));
  const scrollRight = () =>
    scrollToStop(
      Math.min((quoteResponse?.stops.length || 1) - 1, currentStopIndex + 1)
    );

  const canScrollLeft = currentStopIndex > 0;
  const canScrollRight =
    quoteResponse && currentStopIndex < quoteResponse.stops.length - 1;

  if (!quoteResponse) return null;

  const kencoQuote = quoteResponse.quotes.find(
    (q) =>
      q.source === Quoting.KencoFlash &&
      q.type === QuoteTypeInSource.KencoAverage
  );

  let kencoTransitDays, kencoLayoverDays, showKencoTransitLayover;
  if (kencoQuote && kencoQuote.metadata) {
    const { metadata } = kencoQuote;

    kencoTransitDays = metadata.calculated_transit_days;
    kencoLayoverDays = metadata.billable_layover_days;
    showKencoTransitLayover =
      kencoTransitDays != null || kencoLayoverDays != null;
  }

  return (
    <>
      <div className='w-full h-0.5 bg-neutral-300 my-2' />

      <div className='relative w-full'>
        {/* Stops container with collapse/expand functionality */}
        {quoteResponse.stops.length <= 2 ? (
          // For 2 or fewer stops, always show expanded view
          <Flex direction='row' gap='sm' align='center' justify='center'>
            {quoteResponse.stops.map((stop, index) => {
              const leg = routeDetails?.legs?.find(
                (l) => l.startStopIndex === stop.order
              );
              return (
                <React.Fragment key={index}>
                  <Typography
                    className={cn(
                      'whitespace-nowrap',
                      quoteResponse.stops.length === 2 && 'text-sm'
                    )}
                    variant='body-xs'
                    weight='medium'
                  >
                    {toTitleCase(stop.city)},
                    {quoteResponse.stops.length > 2 && <br />}
                    {quoteResponse.stops.length === 2 && ' '}
                    {stop.state}
                  </Typography>
                  {index < quoteResponse.stops.length - 1 && (
                    <Flex direction='col' align='center'>
                      {quoteResponse.stops.length > 2 &&
                        leg &&
                        leg.distanceMiles !== undefined && (
                          <Typography
                            variant='body-xxs'
                            className='text-neutral-500 mb-[-2px] text-center whitespace-nowrap'
                          >
                            {`${_.round(leg.distanceMiles).toLocaleString()} mi`}
                          </Typography>
                        )}
                      <ArrowRightIcon
                        className={cn(
                          'w-3 h-3 text-neutral-500',
                          quoteResponse.stops.length === 2 ? 'w-4 h-4' : ''
                        )}
                      />
                    </Flex>
                  )}
                </React.Fragment>
              );
            })}
          </Flex>
        ) : (
          // For 3+ stops, show collapsed or expanded view
          <>
            {!stopsExpanded ? (
              // Collapsed view: first stop → count → last stop
              <Tooltip delayDuration={100}>
                <TooltipTrigger asChild>
                  <Flex
                    direction='row'
                    gap='sm'
                    align='center'
                    justify='center'
                    className='w-max max-w-full cursor-pointer hover:bg-neutral-50 rounded p-1 transition-colors mx-auto'
                    onClick={() => setStopsExpanded(true)}
                  >
                    <Typography
                      variant='body-xs'
                      className='whitespace-nowrap'
                      weight='medium'
                    >
                      {toTitleCase(quoteResponse.stops[0].city)}
                      ,
                      <br />
                      {quoteResponse.stops[0].state}
                    </Typography>
                    <Flex direction='col' align='center'>
                      <Typography
                        variant='body-xxs'
                        className='text-neutral-600 mb-1 text-center whitespace-nowrap font-medium rounded-full px-1.5 py-0.5 cursor-pointer border-transparent hover:border-brand-500 hover:text-brand-500  hover:scale-105 transition-all duration-100 '
                      >
                        {`+${quoteResponse.stops.length - 2} stop${
                          quoteResponse.stops.length - 2 === 1 ? '' : 's'
                        }`}
                      </Typography>
                      <ArrowRightIcon className='w-3 h-3 text-neutral-500' />
                    </Flex>
                    <Typography
                      variant='body-xs'
                      weight='medium'
                      className='whitespace-nowrap'
                    >
                      {toTitleCase(
                        quoteResponse.stops[quoteResponse.stops.length - 1].city
                      )}
                      ,
                      <br />
                      {
                        quoteResponse.stops[quoteResponse.stops.length - 1]
                          .state
                      }
                    </Typography>
                  </Flex>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Expand stops</p>
                </TooltipContent>
              </Tooltip>
            ) : (
              // Expanded view with carousel controls
              <Flex direction='col' gap='sm' className='w-full mx-0'>
                <div className='flex items-center justify-center w-full'>
                  {/* Left navigation button */}
                  <div className='shrink-0 mr-2'>
                    <button
                      onClick={scrollLeft}
                      disabled={!canScrollLeft}
                      type='button'
                      className={cn(
                        'p-1 rounded-full transition-colors',
                        canScrollLeft
                          ? 'hover:bg-neutral-100 text-neutral-500'
                          : 'text-neutral-300 cursor-not-allowed'
                      )}
                      aria-label='Scroll left'
                    >
                      <ChevronLeftIcon className='w-4 h-4' />
                    </button>
                  </div>

                  {/* Stops container wrapper */}
                  <div className='flex-1 overflow-hidden px-2'>
                    <Flex
                      direction='row'
                      gap='sm'
                      align='start'
                      className='w-full overflow-x-auto [&::-webkit-scrollbar]:hidden'
                      ref={stopsContainerRef}
                    >
                      {quoteResponse.stops.map((stop, index) => {
                        const leg = routeDetails?.legs?.find(
                          (l) => l.startStopIndex === stop.order
                        );
                        return (
                          <React.Fragment key={index}>
                            <Typography
                              variant='body-xs'
                              className='whitespace-nowrap shrink-0 qq-stop-label'
                            >
                              {toTitleCase(stop.city)},
                              <br />
                              {stop.state}
                            </Typography>
                            {index < quoteResponse.stops.length - 1 && (
                              <Flex direction='col' align='center'>
                                {leg && leg.distanceMiles !== undefined && (
                                  <Typography
                                    variant='body-xxs'
                                    className='text-neutral-600 mb-[-2px] text-center whitespace-nowrap'
                                  >
                                    {`${_.round(leg.distanceMiles).toLocaleString()} mi`}
                                  </Typography>
                                )}
                                <ArrowRightIcon className='w-3 h-3 text-neutral-500' />
                              </Flex>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </Flex>
                  </div>

                  {/* Right navigation button */}
                  <div className='shrink-0 ml-2'>
                    <button
                      onClick={scrollRight}
                      disabled={!canScrollRight}
                      type='button'
                      className={cn(
                        'p-1 rounded-full transition-colors',
                        canScrollRight
                          ? 'hover:bg-neutral-100 text-neutral-500'
                          : 'text-neutral-300 cursor-not-allowed'
                      )}
                      aria-label='Scroll right'
                    >
                      <ChevronRightIcon className='w-4 h-4' />
                    </button>
                  </div>
                </div>

                {/* Scroll hint for multiple stops */}
                {quoteResponse.stops.length > 3 && (
                  <div className='text-xs text-neutral-500 mt-1 text-center'>
                    ← Scroll to view more stops →
                  </div>
                )}
              </Flex>
            )}
          </>
        )}
      </div>

      <Flex
        direction='row'
        gap='md'
        className='mt-0.5 w-full'
        align='center'
        justify='center'
      >
        {maxDistance > 0 && (
          <>
            <Typography variant='body-xs'>
              {`Est. Total Distance: ${_.round(maxDistance).toLocaleString()} miles`}
            </Typography>

            {/* Tooltip for distance source (GS, DAT, E2Open, FreightView, etc) */}
            <Tooltip delayDuration={10}>
              <TooltipTrigger asChild>
                <InfoIcon className='h-4 w-4' />
              </TooltipTrigger>
              <TooltipContent>
                {distanceSource && (
                  <Typography variant='body-xs'>
                    {`Distance from ${getMileageSourceLabel(distanceSource)}`}
                  </Typography>
                )}
              </TooltipContent>
            </Tooltip>
          </>
        )}

        {/* Link to Google Map route */}
        <Tooltip delayDuration={10}>
          <TooltipTrigger asChild>
            <MapIcon
              className='cursor-pointer h-4 w-4 hover:stroke-brand!'
              onClick={() =>
                openExternalUrl(constructGoogleMapsUrl(quoteResponse.stops))
              }
            />
          </TooltipTrigger>
          <TooltipContent>View route in Google Maps</TooltipContent>
        </Tooltip>
      </Flex>

      {showKencoTransitLayover && (
        <Flex
          direction='row'
          align='center'
          justify='center'
          gap='md'
          className='w-full flex-wrap text-sm'
        >
          {kencoTransitDays != null && (
            <Typography variant='body-xs' className='text-neutral-500'>
              Transit Days: {kencoTransitDays}
            </Typography>
          )}
          {kencoTransitDays != null && kencoLayoverDays != null && (
            <span className='text-neutral-300' aria-hidden>
              |
            </span>
          )}
          {kencoLayoverDays != null && (
            <Typography variant='body-xs' className='text-neutral-500'>
              Layover Days: {kencoLayoverDays}
            </Typography>
          )}
        </Flex>
      )}

      <QuickQuoteCurrencyToggle />
    </>
  );
};
